import { getQueryClientInstance } from '@/api/queries/queryClient';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const COUNTRY_OVERRIDE_STORAGE_KEY = 'ces-country-override';

interface CountryOverrideState {
  countries: string[];
  setCountries: (countries: string[]) => void;
  clear: () => void;
}

// Trim, drop blanks, upper-case, and dedupe while preserving selection order so the header value is
// stable and the backend receives canonical CESAPP_XX tokens.
const normalizeCountries = (countries: string[]): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of countries) {
    const code = raw.trim().toUpperCase();
    if (code && !seen.has(code)) {
      seen.add(code);
      normalized.push(code);
    }
  }
  return normalized;
};

// Drop the whole cache (not just invalidate) so no all-access results linger on screen after the
// simulated scope changes; queries then refetch with the new (or absent) x-locale-override header.
const resetCubeCache = (): void => {
  getQueryClientInstance().clear();
};

const useCountryOverrideStore = create<CountryOverrideState>()(
  persist(
    (set): CountryOverrideState => ({
      countries: [],
      setCountries: (countries: string[]) => {
        set({ countries: normalizeCountries(countries) });
        resetCubeCache();
      },
      clear: () => {
        set({ countries: [] });
        resetCubeCache();
      },
    }),
    {
      name: COUNTRY_OVERRIDE_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state: CountryOverrideState) => ({ countries: state.countries }),
    },
  ),
);

export default useCountryOverrideStore;
